import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LOGIN_USER_SUCCESS } from '../../constants/userConstants';
import BackdropLoader from '../Layouts/BackdropLoader';
import MetaData from '../Layouts/MetaData';
import FormSidebar from './FormSidebar';
import { useLang, uiText } from '../../utils/i18n';

// Shared verification screen for both "verify your new account's email"
// and "log in with a one-time code" — same code entry UI either way,
// only the copy and post-verify destination differ.
const VerifyOtp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { lang } = useLang();
    const [searchParams] = useSearchParams();

    const email = searchParams.get('email') || '';
    const purpose = searchParams.get('purpose') === 'login' ? 'login' : 'verify_email';

    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(60);

    useEffect(() => {
        if (!email) {
            navigate('/login');
        }
    }, [email, navigate]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const handleVerify = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            return enqueueSnackbar(uiText('enterSixDigitCode', lang), { variant: 'warning' });
        }

        setLoading(true);
        try {
            const { data } = await axios.post('/api/v1/verify-otp', { email, otp, purpose });
            dispatch({ type: LOGIN_USER_SUCCESS, payload: data.user });
            enqueueSnackbar(purpose === 'verify_email' ? uiText('emailVerified', lang) : uiText('loggedIn', lang), { variant: 'success' });
            navigate('/');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || uiText('verificationFailed', lang), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleResend = useCallback(async () => {
        if (cooldown > 0) return;
        try {
            await axios.post('/api/v1/resend-otp', { email, purpose });
            enqueueSnackbar(uiText('newCodeSent', lang), { variant: 'success' });
            setCooldown(60);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || uiText('couldNotResend', lang), { variant: 'error' });
        }
    }, [email, purpose, cooldown, enqueueSnackbar, lang]);

    return (
        <>
            <MetaData title="Verify Code | 1313 Mart" />
            {loading && <BackdropLoader />}

            <main className="w-full mt-12 sm:pt-20 sm:mt-0">
                <div className="flex sm:w-4/6 sm:mt-4 m-auto mb-7 bg-white shadow-lg">

                    <FormSidebar
                        title={purpose === 'verify_email' ? uiText('almostThere', lang) : uiText('otpLoginTitle', lang)}
                        tag={purpose === 'verify_email' ? uiText('verifyEmailTag', lang) : uiText('verifyLoginTag', lang)}
                    />

                    <div className="flex-1 overflow-hidden">
                        <div className="text-center py-10 px-4 sm:px-14">
                            <h2 className="text-xl font-medium text-gray-800 mb-1">{uiText('enterCodeSentTo', lang)}</h2>
                            <p className="text-sm text-primary-blue font-medium mb-6">{email}</p>

                            <form onSubmit={handleVerify}>
                                <div className="flex flex-col w-full gap-4">
                                    <TextField
                                        fullWidth
                                        label={uiText('sixDigitCode', lang)}
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        inputProps={{ inputMode: 'numeric', maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '20px' } }}
                                        autoFocus
                                    />

                                    <button type="submit" className="text-white py-3 w-full bg-primary-orange shadow hover:shadow-lg rounded-sm font-medium">
                                        {uiText('verify', lang)}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        disabled={cooldown > 0}
                                        className="text-sm font-medium text-primary-blue py-2 disabled:text-gray-400"
                                    >
                                        {cooldown > 0 ? `${uiText('resendCodeIn', lang)} ${cooldown}s` : uiText('resendCode', lang)}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default VerifyOtp;